import { useContext, useMemo, useState, useId } from "react";
import PropTypes from "prop-types";

import { LuxeContext } from "@context/LuxeProvider";
import { WishlistContext } from "@context/wishlistProvider";
import { useTracking } from "@context/Tracking";

import { createCookie, gaCustomEvent } from "@helpers/browser";
import { isOutOfStock, isProductPreOrder } from "@helpers/product";
import { withALErrorBoundary } from "@helpers/ErrorBoundary/ALErrorBoundary";
import { cn } from "@helpers/general";

import { useCurrentCountryCode, useProductPricing } from "@hooks/usePrices";
import useGetABTest from "@hooks/useCustomABTest";
import { useLocalizedSentenceDict } from "@hooks/useSentenceDict";
import useCart from "@hooks/useCart";
import useCheckInventory from "@hooks/useCheckInventory";
import useDeviceDetect from "@hooks/useDeviceDetect";
import useDiscountsInfo from "@hooks/useDiscountsInfo";

import HeartIcon from "@svgs/heart.svg";
import { ActionType } from "@constants/ActionType";
import { ComponentType } from "@constants/ComponentType";
import { HandleType } from "@constants/HandleType";
import { Country } from "@constants/Country";
import DISCOUNT_TYPES from "@constants/DiscountTypes";
import RESTRICTED_HTS_PER_COUNTRY from "@constants/RestrictedHtsPerCountry";

import PriceContainer from "@components/PriceContainer";
import QuickAddToCart from "@components/QuickAddToCart";
import ProductReviewRating from "@components/ProductPage/ProductReviewRating";
import ProductImage from "./productCollection/ProductImage";
import ProductAlternatives from "../ProductAlternatives";
import { ALLink as Link, ALTag, ALButtonV2 } from "../ALComponents";

import { GIFT_PACKAGES_HANDLES } from "@constants/GiftPackages";

function ProductCollectionOnClick({ to, onClick, children, searchObject, ...rest }) {
  if (
    rest.componentType === ComponentType.CP_BYOB ||
    rest.componentType === ComponentType.CP_BYOB_QUICKVIEW
  ) {
    return (
      <div
        onClick={() =>
          rest.isMobile && !rest.isBYOBBundle ? rest.setPopupProduct(rest.product) : null
        }
        {...rest}
      >
        {children}
      </div>
    );
  }

  if (rest.componentType === ComponentType.MTO_APP) {
    return (
      <button type="button" onClick={onClick}>
        {children}
      </button>
    );
  }
  return (
    <Link to={to} state={{ searchObject }} onClick={onClick} {...rest}>
      {children}
    </Link>
  );
}

function ProductCollectionContent(props) {
  const [isHovered, setIsHovered] = useState(false);
  const cardId = useId();

  const {
    collectionHandle,
    collectionTitle,
    product: productWithOldInventoryData,
    variantId,
    componentType,
    searchObject,
    className,
    setActiveAlternative,
    prices,
    setPopupProduct,
    isBYOBBundle,
    topProductIndex,
    hideQuickAdd,
    onCustomize,
  } = props;
  const contentfulProduct = productWithOldInventoryData.contentful.node;
  const isMtoApp = componentType === ComponentType.MTO_APP;

  const wishlistContext = useContext(WishlistContext);
  const {
    luxe: { isLuxeActive, hasLuxeInCart, shouldAddLuxeInNextATC },
  } = useContext(LuxeContext);

  const discountsInfo = useDiscountsInfo();
  const dict = useLocalizedSentenceDict();
  const { trackProductClick } = useTracking();
  const countryCode = useCurrentCountryCode();
  const { isMobile } = useDeviceDetect();

  const giftDiscount =
    (discountsInfo || []).find((s) => s.type === DISCOUNT_TYPES.GIFT_WITH_PURCHASE) || null;

  const price = useMemo(
    () => prices?.find((p) => p?.node?.country === countryCode)?.node,
    [prices, countryCode]
  );
  const { variants: productVariantsInventory } = useCheckInventory(
    productWithOldInventoryData.contentful.node.handle
  );

  const productWithInventory = useMemo(
    () => ({
      ...productWithOldInventoryData,
      node: {
        ...productWithOldInventoryData.node,
        variants: productWithOldInventoryData.node.variants.map((v) => {
          const currentVariantInventory = productVariantsInventory.find(
            (variantInventory) => variantInventory?.sku === v?.sku
          );
          return currentVariantInventory
            ? { ...v, availableForSale: currentVariantInventory.availableForSale }
            : v;
        }),
      },
    }),
    [productWithOldInventoryData, productVariantsInventory]
  );

  const {
    products: [productPrices],
    isInitialLoading: pricesLoading,
  } = useProductPricing({
    products: [productWithInventory],
    allLocalPrices: price ? [{ node: price }] : [],
  });
  const shopifyProduct = productWithInventory.node;
  const { variants, handle, shopifyId: productId, title, productType } = shopifyProduct;
  const customizationLabel = isMtoApp
    ? contentfulProduct.productDetails?.customizableType?.join(", ")
    : "";

  const isMTO =
    contentfulProduct?.madeToOrderTimelines &&
    contentfulProduct?.madeToOrderTimelines !== "null" &&
    contentfulProduct?.madeToOrderTimelines !== "";

  let variant = null;
  if (searchObject?.isSearch) {
    variant = productWithInventory.variant;
  }

  const isGiftProduct =
    giftDiscount &&
    giftDiscount?.freegift?.products?.some(
      (giftProduct) => giftProduct.handle === shopifyProduct.handle
    );
  const isGiftPackaging = GIFT_PACKAGES_HANDLES.includes(handle);

  const isSoldOut = isOutOfStock(shopifyProduct, countryCode) && handle !== HandleType.GIFT_CARD;
  const isPreOrder = isProductPreOrder(contentfulProduct);

  const isLuxe = isLuxeActive || hasLuxeInCart || shouldAddLuxeInNextATC;

  // Check if all products are not available
  const hasNoVariantsAvailable = variants?.every(
    (x) => x.availableForSale === true && x.inventoryPolicy !== "DENY"
  );

  // Only display PreOrder content when use is browsing US country
  const shouldDisplayPreOrder = isPreOrder && countryCode === Country.US && hasNoVariantsAvailable;

  const { quickAddToCart } = useCart(productPrices, collectionHandle, isLuxe, componentType);

  let linkToPDP = `/${
    componentType === ComponentType.PDP_ADS && useGetABTest(0.5, "lp_ads") === "A"
      ? "p"
      : "products"
  }/${contentfulProduct.handle}/`;

  if (variant?.selectedOptions?.length > 0) {
    linkToPDP = linkToPDP.concat(
      `?${variant.selectedOptions[0].name}=${encodeURIComponent(variant.selectedOptions[0].value)}`
    );
  }

  if (searchObject?.isSearch && isMTO && productWithOldInventoryData.productPath) {
    linkToPDP = `/products/${productWithOldInventoryData.productPath}`;
  }

  if (componentType === ComponentType.TOP_PRODUCTS) {
    linkToPDP = `/products/${contentfulProduct.handle}?origin_content=top_products_${collectionHandle}`;
  }

  const isPurchaseRestricted = !!(RESTRICTED_HTS_PER_COUNTRY[countryCode] || []).find((code) =>
    productWithInventory?.contentful?.node?.hts?.startsWith(code)
  );

  if (!shopifyProduct) {
    return null;
  }

  const toggleWishlist = (e, action) => {
    e.preventDefault();
    if (action === ActionType.ADD) {
      gaCustomEvent(`${collectionTitle} - Add to Wishlist`, "Click", shopifyProduct.title);
      if (shopifyProduct) {
        wishlistContext.addItemToWishlist({
          type: shopifyProduct.productType,
          productHandle: shopifyProduct.handle,
          productName: shopifyProduct.title,
          productId,
          price: productPrices?.price,
        });
      }
    }
    if (action === ActionType.REMOVE) {
      gaCustomEvent(`${collectionTitle} - Remove from Wishlist`, "Click", shopifyProduct.title);
      if (shopifyProduct) {
        wishlistContext.removeItemFromWishlist({
          productHandle: shopifyProduct.handle,
          productId,
          price: productPrices?.price,
        });
      }
    }
  };
  if (!contentfulProduct) {
    return null;
  }

  const productCollectionClick = () => {
    if (typeof props.onClick === "function") {
      props.onClick();
    }

    // Set Cookies / Provider [TBD] - collection handle & title
    if (collectionHandle) {
      createCookie("bread_url", `/collections/${collectionHandle}`, 2);
      createCookie("bread_title", collectionTitle, 2);
    }

    trackProductClick({
      productId: productId.replace("gid://shopify/Product/", ""),
      price: variants[0].price,
      title,
      category: productType,
    });
  };

  let canQuickAdd =
    (isMobile && !isSoldOut && !shouldDisplayPreOrder && !isPurchaseRestricted && !isGiftProduct) ||
    ComponentType.CP_BYOB === componentType ||
    ComponentType.CP_BYOB_QUICKVIEW === componentType;

  if (ComponentType.CP_BYOB && isBYOBBundle) {
    canQuickAdd = false;
  }
  if (isGiftPackaging) {
    canQuickAdd = false;
  }

  const showQuickAdd = !isMtoApp && (isHovered || canQuickAdd) && !pricesLoading && !hideQuickAdd;

  const showWaitlistMessage = isMTO ? countryCode === Country.US : true;
  const isInWishlist = wishlistContext?.wishlistItems?.includes(contentfulProduct.handle);

  const handlePointerOver = () =>
    isBYOBBundle || isGiftPackaging ? setIsHovered(false) : setIsHovered(true);

  const handlePointerOut = (e) => {
    const next = e.relatedTarget; // may be null
    const current = e.currentTarget;

    // Still inside the card?
    if (next && current.contains(next)) return;

    // If CTA is rendered elsewhere (portal), mark it with data-hover-guard
    const inGuard = next instanceof Element && next.closest?.(`[data-hover-guard="${cardId}"]`);
    if (inGuard) return;

    setIsHovered(false);
  };

  const handleQuickAddToCart = ({ selectedVariant = null } = {}) => {
    if (
      componentType === ComponentType.CP_BYOB ||
      componentType === ComponentType.CP_BYOB_QUICKVIEW
    ) {
      quickAddToCart({ shouldOpenCart: false, selectedVariant });
      setPopupProduct(null); // Close the quick view
    } else {
      quickAddToCart({ selectedVariant });
    }
  };
  let variantPrices = null;
  if (searchObject?.isSearch && productPrices && productPrices?.variants?.length > 0) {
    variantPrices = productPrices.variants.find((v) => v.id === variantId);
  }

  const handleMtoAppClick = (event) => {
    event.preventDefault();
    // TODO: Implement the click behavior for the MTO app.
    onCustomize();
  };

  const [part0, part1] = contentfulProduct.title.split(" - ");
  const isMTOSearch = searchObject?.isSearch && isMTO;

  const productTitle = isMTOSearch ? part0 : part1;
  const materials = contentfulProduct.productDetails?.components;
  const material =
    isMtoApp && materials?.find((value) => ["Gold Vermeil", "10K Solid Gold"].includes(value));
  const productSubtitle = isMTOSearch ? "" : part0;

  return (
    <div className={cn(className)}>
      {componentType !== ComponentType.TOP_PRODUCTS && (
        <ALTag
          product={{ ...productPrices, contentful: productPrices.contentful.node }}
          componentType={isMtoApp ? ComponentType.MTO_APP : ComponentType.CP}
          shouldDisplayPreOrder={shouldDisplayPreOrder}
          discountsInfo={discountsInfo}
        />
      )}

      {componentType === ComponentType.TOP_PRODUCTS ? (
        <p className="title-xxl absolute z-[1] mobile:z-[2] text-secondary pt-[5px] pl-[10px] desktop:pl-[15px]">
          {topProductIndex}
        </p>
      ) : null}

      <div>
        <div className="h-full mobile:relative">
          <div className="h-full flex relative aspect-[0.8] mobile:flex-none mobile:overflow-visible mobile:p-0 cursor-pointer group/product-card">
            {componentType === ComponentType.WISHLIST && (
              <button
                type="button"
                className="absolute top-[10px] right-[10px] z-10 flex justify-center items-center"
                onClick={(e) =>
                  toggleWishlist(e, isInWishlist ? ActionType.REMOVE : ActionType.ADD)
                }
              >
                <HeartIcon
                  width="25"
                  height="25"
                  stroke="#000000"
                  fill={isInWishlist ? "#000000" : "none"}
                />
              </button>
            )}

            {isMtoApp && !pricesLoading && (
              <div className="absolute m-0 z-10 left-[10px] right-[10px] bottom-[14px] mobile:bottom-[12px]">
                <ALButtonV2 type="button" variant="outline" fullWidth onClick={handleMtoAppClick}>
                  {dict.get("Customize It")}
                </ALButtonV2>
              </div>
            )}

            {showQuickAdd && (
              <div
                data-hover-guard={cardId}
                className="absolute m-0 z-10 left-[10px] right-[10px] bottom-[14px] mobile:bottom-[12px]"
                onPointerOver={handlePointerOver}
                onPointerOut={handlePointerOut}
              >
                <QuickAddToCart
                  product={{
                    ...shopifyProduct,
                    id: shopifyProduct.shopifyId,
                  }}
                  onQuickAddToCart={handleQuickAddToCart}
                  collectionTitle={collectionTitle}
                  componentType={componentType}
                />
              </div>
            )}

            <ProductCollectionOnClick
              to={isMtoApp ? undefined : linkToPDP}
              searchObject={searchObject}
              onClick={isMtoApp ? handleMtoAppClick : productCollectionClick}
              componentType={componentType}
              className="w-full h-full"
              onPointerOver={handlePointerOver}
              onPointerOut={handlePointerOut}
              setPopupProduct={setPopupProduct}
              product={productWithInventory}
              isMobile={isMobile}
              isBYOBBundle={isBYOBBundle}
            >
              <ProductImage
                shopifyProduct={shopifyProduct}
                componentType={componentType}
                variant={variant}
              />
            </ProductCollectionOnClick>
          </div>
        </div>
      </div>

      {isMtoApp ? (
        <button
          type="button"
          onClick={handleMtoAppClick}
          className="mt-[15px] mobile:mt-[10px] text-left"
        >
          <div>
            <h3 className="typo-body font-bold m-0 mobile:mb-[1px]">{productTitle}</h3>
            {material && <h2 className="typo-micro text-dark-grey">{material}</h2>}
          </div>
          <ProductReviewRating componentType={componentType} productId={productId} />
          <div className="block mt-[10px]">
            <PriceContainer
              productId={productId}
              selectedVariant={productWithOldInventoryData.variant}
              prices={productPrices}
              variantPrices={variantPrices}
              componentType={ComponentType.MTO_APP}
              tags={shopifyProduct.tags}
              pricesLoading={productPrices === undefined}
            />
            {customizationLabel && (
              <p className="typo-micro italic mt-[10px]">{customizationLabel}</p>
            )}
          </div>
        </button>
      ) : (
        <ProductCollectionOnClick
          to={linkToPDP}
          searchObject={searchObject}
          onClick={productCollectionClick}
          componentType={componentType}
          className="block mt-[15px] mobile:mt-[10px]"
          setPopupProduct={setPopupProduct}
          product={productWithInventory}
          isMobile={isMobile}
        >
          <>
            <h3 className="typo-body font-bold m-0 mobile:mb-[1px]">{productTitle}</h3>
            <h2 className="typo-micro text-dark-grey mb-[10px] mobile:mb-[8px]">
              {productSubtitle}
            </h2>
            {!isSoldOut && (
              <PriceContainer
                productId={productId}
                selectedVariant={productWithOldInventoryData.variant}
                prices={productPrices}
                variantPrices={variantPrices}
                componentType={ComponentType.CP}
                tags={shopifyProduct.tags}
                pricesLoading={productPrices === undefined}
              />
            )}
          </>
          {isSoldOut && (
            <p className="product_collection_soldout al_subtext">
              {dict.get("Out of stock")}.
              {showWaitlistMessage && (
                <>
                  &nbsp;<u>{dict.get("Sign up for the wait list.")}</u>
                </>
              )}
            </p>
          )}
        </ProductCollectionOnClick>
      )}
      {!isMtoApp &&
        contentfulProduct?.productAlternatives &&
        componentType !== ComponentType.TOP_PRODUCTS && (
          <ProductAlternatives
            handle={contentfulProduct?.handle}
            productAlternatives={contentfulProduct.productAlternatives}
            componentType={ComponentType.CP}
            handleAlternativeClick={setActiveAlternative}
          />
        )}

      {/* Preorder + No variant available at all */}
      {isMobile && shouldDisplayPreOrder ? (
        <Link
          className="typo-body font-bold underline"
          to={linkToPDP}
          onClick={productCollectionClick}
        >
          {!hasNoVariantsAvailable ? "View Product" : "Pre-order now"}
        </Link>
      ) : null}
    </div>
  );
}

ProductCollectionContent.propTypes = {
  collectionHandle: PropTypes.string,
  collectionTitle: PropTypes.string,
  product: PropTypes.shape({
    contentful: PropTypes.shape({
      node: PropTypes.object,
    }),
    node: PropTypes.shape({
      title: PropTypes.string,
      handle: PropTypes.string,
      variants: PropTypes.array,
      shopifyId: PropTypes.string.isRequired,
    }),
  }).isRequired,
  variantId: PropTypes.string,
  componentType: PropTypes.string.isRequired,
  searchObject: PropTypes.object,
  className: PropTypes.string.isRequired,
  setActiveAlternative: PropTypes.func.isRequired,
  prices: PropTypes.array,
  setPopupProduct: PropTypes.func,
  isBYOBBundle: PropTypes.bool,
  topProductIndex: PropTypes.number,
  onClick: PropTypes.func,
  hideQuickAdd: PropTypes.bool,
  onCustomize: PropTypes.func,
};

export default withALErrorBoundary({
  name: "ProductCollectionContent",
  priority: "P2",
})(ProductCollectionContent);
